import { intercomService } from "@services/intercomService";
import {
  WSAvatar,
  WSButton,
  WSFlexBox,
  WSForm,
  WSGrid,
  WSImage,
  WSInfoBox,
  WSInputMask,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText,
  toWSDateString,
  useWSModalCustom,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import { ExternalBankAccount } from "@wingspanhq/wingspan-api-sdk";
import * as Yup from "yup";
import imageTransactions from "../../../assets/images/microDepositTransactions.png";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesExternalBankAccounts } from "@query";
import { selectorExternalBankAccountLabel } from "../selectors/selectorExternalBankAccountLabel";

/**
 * A masked amount reads `$0.42`; the stored value is dollars.
 *
 * Returns `null` for anything that is not a complete, non-zero amount, which is
 * what both the submit guard and the validation schema key on. The mask always
 * yields a `$0.` prefix, so the pre-migration checks for a literal `"00"` never
 * matched and let `$0.00` through.
 */
export const parseMicrodepositAmount = (
  amount: string | null | undefined
): number | null => {
  const cents = Number(String(amount ?? "").replace(/[^0-9]/g, ""));
  return Number.isFinite(cents) && cents > 0 ? cents / 100 : null;
};

type Props = {
  externalBankAccount: ExternalBankAccount;
} & WSModalCustomProps<boolean>;

type FormData = { amount1: string | null; amount2: string | null };

const AMOUNT_FIELD = {
  component: WSInputMask,
  componentProps: {
    mask: "$0.99",
    placeholder: "$0.00",
    slotChar: "0"
  }
} as const;

const amountSchema = (label: string) =>
  Yup.string()
    .required(`${label} is required`)
    .test(
      "nonZeroAmount",
      "Enter the amount that was deposited",
      value => parseMicrodepositAmount(value) !== null
    );

export const ModalMicrodepositsVerify: React.FC<Props> = ({
  externalBankAccount,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const accountLabel = selectorExternalBankAccountLabel(externalBankAccount);

  const [verify, verifyMeta] =
    queriesExternalBankAccounts.verifyExternalBankAccount.useMutation({
      onSuccess: () => {
        openSnackbar({
          type: "success",
          icon: { name: "check-circle" },
          message: "You’re all set! The payment account was verified"
        });
        onClose(true);
      }
    });

  return (
    <WSForm<FormData>
      defaultValues={{ amount1: null, amount2: null }}
      validationSchema={Yup.object().shape({
        amount1: amountSchema("First amount"),
        amount2: amountSchema("Second amount")
      })}
      onSubmit={data => {
        verify({
          bankAccountId: externalBankAccount.id,
          microDepositVerification: {
            amounts: [
              parseMicrodepositAmount(data.amount1) ?? 0,
              parseMicrodepositAmount(data.amount2) ?? 0
            ]
          }
        });
      }}
    >
      <WSModal divider size="S" title="Verify Account" onClose={onClose}>
        <WSList gap="L">
          <WSText.ParagraphSm>
            Wingspan made <b>two deposits</b> in USD into {accountLabel} on{" "}
            {toWSDateString(new Date(), "monthDate")}. Enter the amounts to
            verify your account.
          </WSText.ParagraphSm>

          <WSInfoBox>
            <WSFlexBox.CenterY mb="XL">
              <WSAvatar.Icon
                mr="M"
                icon="bank"
                color="white"
                colorBackground="gray500"
              />
              <WSText>{accountLabel} statement</WSText>
            </WSFlexBox.CenterY>

            <WSImage mb="2XL" src={imageTransactions} placeholder="" />

            <WSText mb="M">Enter amounts:</WSText>

            <WSGrid gutter="M">
              <WSGrid.Item span={{ xs: "12", s: "6" }}>
                <WSForm.Field
                  {...AMOUNT_FIELD}
                  label="First amount"
                  name="amount1"
                />
              </WSGrid.Item>
              <WSGrid.Item span={{ xs: "12", s: "6" }}>
                <WSForm.Field
                  {...AMOUNT_FIELD}
                  label="Second amount"
                  name="amount2"
                />
              </WSGrid.Item>
            </WSGrid>
          </WSInfoBox>

          <ErrorAlert
            context="BankAccountVerification"
            error={verifyMeta.error}
          />

          <WSForm.Values names={["amount1", "amount2"]}>
            {values => (
              <WSButton
                fullWidth
                disabled={
                  parseMicrodepositAmount(values.amount1) === null ||
                  parseMicrodepositAmount(values.amount2) === null
                }
                loading={verifyMeta.isLoading}
              >
                Verify bank account
              </WSButton>
            )}
          </WSForm.Values>

          <WSText.ParagraphSm color="gray500">
            Payments to invoices made from this account will be in pending
            status until the verification process is complete. Once the account
            is verified, invoices paid with this account will be processed.
          </WSText.ParagraphSm>

          <WSFlexBox.CenterY>
            <WSText.ParagraphXs color="gray400" mr="XS">
              Didn’t receive the deposits?
            </WSText.ParagraphXs>
            <WSButton.Link
              size="S"
              onClick={intercomService.open}
              type="button"
            >
              Contact support
            </WSButton.Link>
          </WSFlexBox.CenterY>
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalMicrodepositsVerify = () =>
  useWSModalCustom(ModalMicrodepositsVerify);
